import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DeleteConfirmDialog } from '@components/DeleteConfirmDialog';
import {
  EligibilityAlertsBlock,
  EligibilityCoverageBlock,
  EligibilityFinancialsBlock,
  EligibilitySummaryContent,
  VerificationHistorySection,
} from '@features/eligibility-checks';
import { ReadinessIndicator } from '@features/eligibility-checks/components/ReadinessIndicator';
import { useEligibilityActions } from '@features/eligibility-checks/hooks/useEligibilityActions';
import { useEligibilityBadgeState } from '@features/eligibility-checks/hooks/useEligibilityBadgeState';
import {
  ELIG_TOKENS,
  READINESS_FILLED,
  READINESS_TONE,
} from '@features/eligibility-checks/theme/readinessTokens';
import type { BlockReadiness } from '@features/eligibility-checks/types/readiness';
import { SelfBookedReviewBanner } from '@features/scheduling-settings';
import { useCan } from '@features/user';
import CloseIcon from '@mui/icons-material/Close';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';
import { useAppSelector } from '@store/hooks';
import { formatDistanceToNow } from 'date-fns';
import { useSnackbar } from 'notistack';

import { useNavigateToEncounter } from '../../hooks/useNavigateToEncounter';
import {
  applyOptimisticAppointmentPatch,
  selectOptimisticAppointmentPatches,
  useGetAppointmentQuery,
  useSendAppointmentReminderMutation,
} from '../../store';
import type { Appointment } from '../../types';
import { formatDateTimeRange } from '../../utils/dateTimeFormatters';
import { AppointmentPropertiesTable } from '../AppointmentPropertiesTable';
import { getAppointmentDisplayTitle } from '../CalendarWidget/utils';

import { AppointmentActionsMenu } from './AppointmentActionsMenu';

interface AppointmentDetailDrawerProps {
  open: boolean;
  appointmentId?: string;
  onClose: () => void;
  onAppointmentDelete?: (appointmentId: string, seriesId?: string) => Promise<void>;
}

export const AppointmentDetailDrawer = ({
  open,
  appointmentId,
  onClose,
  onAppointmentDelete,
}: AppointmentDetailDrawerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    data: fetchedAppointment,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAppointmentQuery(
    { appointmentId: appointmentId ?? '' },
    {
      skip: !open || !appointmentId,
      refetchOnMountOrArgChange: true,
    },
  );
  const optimisticAppointmentPatches = useAppSelector(selectOptimisticAppointmentPatches);
  const fetchedAppointmentPatch = fetchedAppointment
    ? optimisticAppointmentPatches[fetchedAppointment.appointment_id]
    : undefined;
  const appointment = applyOptimisticAppointmentPatch(
    fetchedAppointment,
    fetchedAppointmentPatch,
  );
  const isWaitingForAppointment = (isLoading || isFetching) && !appointment;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? '100vw' : 520,
            bgcolor: ELIG_TOKENS.bg,
            color: ELIG_TOKENS.ink,
          },
        },
      }}
    >
      {isWaitingForAppointment ? (
        <CenteredState>
          <CircularProgress size={24} />
        </CenteredState>
      ) : error || !appointment ? (
        <CenteredState>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Appointment details could not be loaded.
          </Alert>
        </CenteredState>
      ) : (
        <DrawerBody
          appointment={appointment}
          onClose={onClose}
          onAppointmentDelete={onAppointmentDelete}
        />
      )}
    </Drawer>
  );
};

const DrawerBody = ({
  appointment,
  onClose,
  onAppointmentDelete,
}: {
  appointment: Appointment;
  onClose: () => void;
  onAppointmentDelete?: (appointmentId: string, seriesId?: string) => Promise<void>;
}) => {
  const patientId = appointment.patient?.patient_id;
  const encounterId = getEncounterId(appointment);
  const patientName = appointment.patient?.name ?? undefined;
  const { openInvoice } = useEligibilityActions();
  const blockState = appointment.block_state ?? null;
  const readiness = blockState?.readiness ?? null;
  const readinessLabel = blockState?.readiness_label ?? getFallbackReadinessLabel(readiness);
  const eligibilityInput = blockState?.readiness_inputs?.eligibility ?? null;
  const readinessSummary = eligibilityInput?.summary;

  const handleSendInvoice = () => {
    openInvoice({
      patientId,
      patientName,
      encounterId,
      appointmentId: appointment.appointment_id,
      memberObligationCents: eligibilityInput?.member_obligation_cents ?? null,
      source: 'calendar_block',
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DrawerHeader
        appointment={appointment}
        readiness={readiness}
        onClose={onClose}
        onAppointmentDelete={onAppointmentDelete}
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        {blockState?.self_booked &&
          blockState.booking_prospect_id &&
          !patientId &&
          appointment.status !== 'cancelled' && (
            <SelfBookedReviewBanner
              appointmentId={appointment.appointment_id}
              bookingProspectId={blockState.booking_prospect_id}
              status={appointment.status}
              onDeclined={onClose}
            />
          )}

        {readiness && readinessLabel && (
          <ReadinessHero
            readiness={readiness}
            label={readinessLabel}
            summary={readinessSummary}
            status={eligibilityInput?.status}
            memberObligationCents={eligibilityInput?.member_obligation_cents}
            lastCheckedAt={eligibilityInput?.last_checked_at}
            expiresAt={eligibilityInput?.expires_at}
            onSendInvoice={readiness === 'collect' ? handleSendInvoice : undefined}
          />
        )}

        <DrawerSection title="Properties">
          <AppointmentPropertiesTable appointment={appointment} interactive />
        </DrawerSection>

        <AppointmentEligibilitySection
          patientId={patientId}
          encounterId={encounterId}
          patientName={patientName}
        />
      </Box>

      <AppointmentDetailPrimaryActions appointment={appointment} variant="footer" />
    </Box>
  );
};

const DrawerHeader = ({
  appointment,
  readiness,
  onClose,
  onAppointmentDelete,
}: {
  appointment: Appointment;
  readiness: BlockReadiness | null;
  onClose: () => void;
  onAppointmentDelete?: (appointmentId: string, seriesId?: string) => Promise<void>;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { appointment: appointmentTracking } = useEventTracking();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const patientId = appointment.patient?.patient_id;
  const patientName = appointment.patient?.name ?? undefined;
  const title = getAppointmentDisplayTitle(appointment);

  // The manual-reminder action mirrors the automated scheduler send (SMS +
  // email, including the reschedule/manage link). It's gated behind the
  // self-serve scheduling rollout and the org-calendar-manager permission,
  // and only makes sense once a patient is assigned to remind.
  const schedulingEnabled = useFeatureGate(FeatureGates.PATIENT_SELF_SCHEDULING);
  const canManageCalendar = useCan('manage_org_calendar');
  const [sendReminder, { isLoading: isSendingReminder }] =
    useSendAppointmentReminderMutation();
  const canSendReminder = schedulingEnabled && canManageCalendar && Boolean(patientId);

  const handleDeleteClick = () => {
    appointmentTracking.actionClicked({
      appointment_id: appointment.appointment_id,
      action: 'delete',
      source: 'schedule',
      has_encounter: Boolean(getEncounterId(appointment)),
    });
    setIsDeleteDialogOpen(true);
  };

  const handleSendReminder = async () => {
    appointmentTracking.actionClicked({
      appointment_id: appointment.appointment_id,
      action: 'send_reminder',
      source: 'schedule',
      has_encounter: Boolean(getEncounterId(appointment)),
    });

    try {
      const result = await sendReminder({
        appointmentId: appointment.appointment_id,
      }).unwrap();
      if (result.success) {
        enqueueSnackbar(
          patientName ? `Reminder sent to ${patientName}.` : 'Reminder sent.',
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar('Couldn’t send the reminder. Please try again.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Failed to send appointment reminder:', error);
      enqueueSnackbar('Couldn’t send the reminder. Please try again.', {
        variant: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!onAppointmentDelete || isDeleting) return;

    setIsDeleteDialogOpen(false);
    setIsDeleting(true);
    try {
      await onAppointmentDelete(appointment.appointment_id, appointment.series?.series_id);
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      enqueueSnackbar('Failed to delete appointment. Please try again.', {
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 2,
          borderBottom: `1px solid ${ELIG_TOKENS.line}`,
          bgcolor: 'background.paper',
        }}
      >
        {readiness && <ReadinessIndicator readiness={readiness} size={12} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component={patientId ? RouterLink : 'div'}
            to={patientId ? `/patient/${patientId}` : undefined}
            variant="h6"
            sx={{
              color: ELIG_TOKENS.midnight,
              display: 'block',
              fontWeight: 650,
              lineHeight: 1.2,
              overflow: 'hidden',
              textDecoration: 'none',
              textOverflow: 'ellipsis',
              '&:hover': patientId
                ? {
                    color: ELIG_TOKENS.midnight,
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  }
                : undefined,
            }}
          >
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
            {formatDateTimeRange(
              new Date(appointment.start_time),
              getAppointmentEndTime(appointment),
            )}
          </Typography>
        </Box>
        <AppointmentActionsMenu
          canSendReminder={canSendReminder}
          isSendingReminder={isSendingReminder}
          onSendReminder={handleSendReminder}
          canDelete={Boolean(onAppointmentDelete)}
          isDeleting={isDeleting}
          onDelete={handleDeleteClick}
        />
        <IconButton onClick={onClose} size="small" aria-label="Close drawer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        type="appointment"
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

type AppointmentDetailPrimaryAction =
  | {
      action: 'record_session';
      label: 'Start Recording';
      supportingText: 'Create the encounter and begin the visit workflow.';
    }
  | {
      action: 'view_session';
      label: 'Go to Encounter';
      supportingText: 'Open the active encounter for this appointment.';
    }
  | {
      action: 'view_session';
      label: 'Go to Note';
      supportingText: 'Open the generated note for this appointment.';
    };

export const getAppointmentDetailPrimaryAction = (
  appointment: Appointment,
): AppointmentDetailPrimaryAction => {
  const encounter: { encounter_id?: string; note_id?: string; status?: string } | null =
    appointment.encounter && typeof appointment.encounter === 'object'
      ? (appointment.encounter as { encounter_id?: string; note_id?: string; status?: string })
      : null;

  if (
    encounter &&
    (encounter.status === 'completed' || encounter.status === 'processing') &&
    encounter.note_id
  ) {
    return {
      action: 'view_session',
      label: 'Go to Note',
      supportingText: 'Open the generated note for this appointment.',
    };
  }

  if (getEncounterId(appointment)) {
    return {
      action: 'view_session',
      label: 'Go to Encounter',
      supportingText: 'Open the active encounter for this appointment.',
    };
  }

  return {
    action: 'record_session',
    label: 'Start Recording',
    supportingText: 'Create the encounter and begin the visit workflow.',
  };
};

export const AppointmentDetailPrimaryActions = ({
  appointment,
  onPrimaryAction,
  variant = 'card',
}: {
  appointment: Appointment;
  onPrimaryAction?: () => void | Promise<void>;
  variant?: 'card' | 'footer';
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { navigateToEncounter, isLoading } = useNavigateToEncounter();
  const { appointment: appointmentTracking } = useEventTracking();
  const primaryAction = getAppointmentDetailPrimaryAction(appointment);

  const handlePrimaryAction = async () => {
    appointmentTracking.actionClicked({
      appointment_id: appointment.appointment_id,
      action: primaryAction.action,
      source: 'schedule',
      has_encounter: Boolean(getEncounterId(appointment)),
    });

    try {
      if (onPrimaryAction) {
        await onPrimaryAction();
      } else {
        await navigateToEncounter(appointment);
      }
    } catch (error) {
      console.error('Failed to open appointment encounter:', error);
      enqueueSnackbar('Failed to open encounter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const isFooter = variant === 'footer';

  return (
    <Box
      sx={{
        mt: 0,
        mb: isFooter ? 0 : 3,
        px: isFooter ? 3 : 2,
        py: isFooter ? 2 : 2,
        borderRadius: '8px',
        border: isFooter ? 'none' : `1px solid ${ELIG_TOKENS.lineStrong}`,
        borderTop: isFooter ? `1px solid ${ELIG_TOKENS.line}` : undefined,
        bgcolor: 'background.paper',
        boxShadow: isFooter ? '0 -8px 24px rgba(28, 30, 133, 0.06)' : undefined,
        flexShrink: isFooter ? 0 : undefined,
      }}
    >
      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={
          isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : primaryAction.action === 'record_session' ? (
            <MicNoneOutlinedIcon />
          ) : (
            <LaunchOutlinedIcon />
          )
        }
        onClick={handlePrimaryAction}
        disabled={isLoading}
        sx={{
          minHeight: 48,
          justifyContent: 'flex-start',
          textTransform: 'none',
          bgcolor: ELIG_TOKENS.midnight,
          color: '#fff',
          fontWeight: 700,
          '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
        }}
      >
        {primaryAction.label}
      </Button>
      {!isFooter && (
        <Typography sx={{ mt: 1, color: ELIG_TOKENS.inkFaint, fontSize: 12, lineHeight: 1.45 }}>
          {primaryAction.supportingText}
        </Typography>
      )}
    </Box>
  );
};

const ReadinessHero = ({
  readiness,
  label,
  summary,
  status,
  memberObligationCents,
  lastCheckedAt,
  expiresAt,
  onSendInvoice,
}: {
  readiness: BlockReadiness;
  label: string;
  summary?: string | null;
  status?: string | null;
  memberObligationCents?: number | null;
  lastCheckedAt?: string | null;
  expiresAt?: string | null;
  onSendInvoice?: () => void;
}) => {
  const tone = READINESS_TONE[readiness];
  const color = ELIG_TOKENS[tone];
  const isFilled = READINESS_FILLED[readiness];
  const isVerified = readiness === 'verified';
  const metaItems = [
    status ? `Eligibility ${formatStatus(status)}` : null,
    memberObligationCents != null ? `${formatCents(memberObligationCents)} due` : null,
    formatRelativeDate('Checked', lastCheckedAt),
    formatRelativeDate('Expires', expiresAt),
  ].filter(Boolean);

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: '8px',
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        borderLeft: `6px solid ${color}`,
        bgcolor: ELIG_TOKENS.card,
        boxShadow: '0 8px 24px rgba(28, 30, 133, 0.06)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            width: 15,
            height: 15,
            mt: 0.55,
            borderRadius: '50%',
            flexShrink: 0,
            ...(isFilled
              ? { bgcolor: color }
              : { border: `2px solid ${color}`, bgcolor: 'transparent' }),
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              color,
              fontWeight: isVerified ? 600 : 700,
              lineHeight: 1.18,
              ...(isVerified
                ? {}
                : {
                    textDecoration: 'underline',
                    textDecorationThickness: '2px',
                    textUnderlineOffset: '3px',
                  }),
            }}
          >
            {label}
          </Typography>
          {summary && (
            <Typography variant="body1" sx={{ color: ELIG_TOKENS.ink, lineHeight: 1.5, mt: 0.75 }}>
              {summary}
            </Typography>
          )}
        </Box>
        {onSendInvoice && (
          <Button
            size="small"
            variant="contained"
            startIcon={<ReceiptOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onSendInvoice}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              bgcolor: ELIG_TOKENS.midnight,
              color: '#fff',
              '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
            }}
          >
            Send invoice
          </Button>
        )}
      </Box>

      {metaItems.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
          {metaItems.map(item => (
            <Box
              key={item}
              component="span"
              sx={{
                px: 0.9,
                py: 0.35,
                borderRadius: '999px',
                border: `1px solid ${ELIG_TOKENS.lineStrong}`,
                color: ELIG_TOKENS.inkSoft,
                bgcolor: ELIG_TOKENS.bg,
                fontSize: 12,
                lineHeight: 1.3,
                fontWeight: 600,
              }}
            >
              {item}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const AppointmentEligibilitySection = ({
  patientId,
  encounterId,
  patientName,
}: {
  patientId?: string;
  encounterId?: string;
  patientName?: string;
}) => {
  const { isHidden, isLoading, eligibilityData } = useEligibilityBadgeState({
    patientId,
    encounterId,
  });

  if (isHidden) {
    return null;
  }

  let content: React.ReactNode;

  if (!patientId && !encounterId) {
    content = (
      <EmptyEligibilityState>
        No appointment eligibility context is available.
      </EmptyEligibilityState>
    );
  } else if (isLoading) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  } else if (!eligibilityData) {
    content = (
      <EmptyEligibilityState>
        No eligibility result has been returned for this appointment yet.
      </EmptyEligibilityState>
    );
  } else {
    content = (
      <Stack spacing={2.5}>
        <EligibilitySummaryContent
          patientId={patientId}
          encounterId={encounterId}
          patientName={patientName}
          source="calendar_block"
          showRerun={!eligibilityData.is_self_pay}
          hideAlerts
          hideSecondaryAction
        />
        {!eligibilityData.is_self_pay && (
          <>
            <Divider sx={{ borderColor: ELIG_TOKENS.line }} />
            <EligibilityAlertsBlock patientId={patientId} encounterId={encounterId} variant="full" />
            <EligibilityFinancialsBlock patientId={patientId} encounterId={encounterId} />
            <EligibilityCoverageBlock patientId={patientId} encounterId={encounterId} />
            <VerificationHistorySection
              patientId={patientId}
              encounterId={encounterId}
              source="calendar_block"
            />
          </>
        )}
      </Stack>
    );
  }

  return (
    <DrawerSection title="Eligibility">
      {content}
    </DrawerSection>
  );
};

const DrawerSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="overline"
      sx={{
        color: ELIG_TOKENS.midnight,
        fontWeight: 700,
        letterSpacing: '0.06em',
        display: 'block',
        mb: 1,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const EmptyEligibilityState = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkFaint, lineHeight: 1.5 }}>
    {children}
  </Typography>
);

const CenteredState = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ p: 3, minHeight: 240, display: 'grid', placeItems: 'center' }}>{children}</Box>
);

const getAppointmentEndTime = (appointment: Appointment): Date => {
  const end = new Date(appointment.start_time);
  end.setMinutes(end.getMinutes() + appointment.duration);
  return end;
};

const getFallbackReadinessLabel = (readiness: BlockReadiness | null): string | null => {
  if (readiness === 'verified') return 'Ready';
  if (readiness === 'collect') return 'Collect payment';
  if (readiness === 'attention') return 'Needs attention';
  if (readiness === 'blocked') return 'Blocked';
  return null;
};

const getEncounterId = (appointment: Appointment): string | undefined =>
  (appointment.encounter as { encounter_id?: string } | null)?.encounter_id;

const formatCents = (cents: number): string => {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

const formatRelativeDate = (label: string, iso?: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${label} ${formatDistanceToNow(date, { addSuffix: true })}`.trim();
};

const formatStatus = (status: string): string => status.replaceAll('_', ' ');
